import { Cookies } from 'quasar'
import { LANGUAGE_COOKIE_EXPIRY_DAYS, LANGUAGE_COOKIE_NAME, LOCALE_TO_COUNTRY_FLAG_MAP, REGION_AVAILABLE_LOCALES, REGION_DEFAULT_LOCALE } from 'src/i18n/language-regions/constants'
import Model from 'src/models/model'

/**
 * Returns the language part of a locale string.
 * @param {string} locale - Locale string
 * @example
 * languageFromLocale('en-US') // 'en'
 */
export function languageFromLocale (locale) {
  return _.first(_.split(locale, '-'))
}

/**
 * Attempts to promote a base language locale to a compatible language-region locale
 * according to the current context's available locales.
 * @param {string} languageLocale - ISO 639-1 language code or full language region locale
 * @returns {string} full language-region or language locale
 * @example
 * languageToLanguageRegion('en') // 'en-US' for 'us' region, 'en-GB' for 'uk' region
 * languageToLanguageRegion('en-US') // 'en-US'
 * languageToLanguageRegion('es') // 'es'
 */
export function languageToLanguageRegion (languageLocale) {
  let availableLocales = _.values(REGION_AVAILABLE_LOCALES)
  const currentCircuit = Model.configuration?.currentCircuit
  if (currentCircuit?.isRemote && currentCircuit?.loaded && currentCircuit?.availableLanguageRegions?.length) {
    availableLocales = currentCircuit.availableLanguageRegions
  }
  return findCompatibleLanguageRegion(languageLocale, availableLocales)
}

/**
 * Returns an available locale that best matches the given locale. Finds best fit by matching
 * primarily on language and secondarily on region, prioritizing language-regions over base languages.
 * If no matching region is found, returns the first locale in the group that matched the language.
 * This is a modified port of the http_accept_language gem's `language_region_compatible_from` method.
 * @param {string} locale - A language/language-region locale string
 * @param {string[]} [availableLocales] - Optional custom list of available locales to search through
 * @returns {string} Compatible available locale
 */
export function findCompatibleLanguageRegion (locale, availableLocales = null) {
  locale = normalizeLocale(locale)
  const language = languageFromLocale(locale)
  availableLocales ||= _.values(REGION_AVAILABLE_LOCALES)

  // First, try to find an exact or compatible match from language-regions only
  const availableLanguageRegions = _.filter(availableLocales, (availableLocale) => _.includes(availableLocale, '-'))
  let exactLanguageRegionMatch
  const compatibleLanguageRegions = _.filter(availableLanguageRegions, (availableLanguageRegion) => {
    if (availableLanguageRegion === locale) {
      exactLanguageRegionMatch = availableLanguageRegion
    }
    return languageFromLocale(availableLanguageRegion) === language
  })
  if (exactLanguageRegionMatch) {
    return exactLanguageRegionMatch
  } else if (compatibleLanguageRegions.length) {
    return _.first(compatibleLanguageRegions)
  }

  // In the unlikely case that no language-region matches were found, try to find a base language match.
  let exactLocaleMatch
  const availableLocalesMatchingLanguage = _.filter(availableLocales, (availableLocale) => {
    if (availableLocale === locale) {
      exactLocaleMatch = availableLocale
    }
    return languageFromLocale(availableLocale) === language
  })
  return exactLocaleMatch || _.first(availableLocalesMatchingLanguage) || REGION_DEFAULT_LOCALE
}

/**
 * Does a simple text-casing conversion to the standard locale format.
 * This should suffice for our use cases, but this does not cover all
 * possible formats where some locales have specific mixed casing.
 * @param {string} locale - A language/language-region locale string
 * @returns {string} Normalized locale string
 * @example normalizeLocale('en-us') // 'en-US'
 */
export function normalizeLocale (locale) {
  const [language, region, script] = _.split(locale, '-')
  return _([_.toLower(language), _.toUpper(region), script]).compact().join('-')
}

/**
 * Attempts to find an appropriate country code to display that country's flag icon
 * to represent the given language. The following is used to determine the country,
 * in order of precedence:
 * 1. Any locale country flag override
 * 2. The region part of the language-region locale, if present
 * @param {string} language - ISO 639-1 language code or full locale code
 * @returns {string | undefined} ISO 3166-1 alpha-2 country code or undefined if not found
 * @example
 * getCountryFlagKeyForLanguage('en') // 'US' for 'us' region, 'GB' for 'uk' region, etc.
 */
export function getCountryFlagKeyForLanguage (language) {
  const languageRegion = languageToLanguageRegion(language)
  if (_.has(LOCALE_TO_COUNTRY_FLAG_MAP, languageRegion)) {
    return LOCALE_TO_COUNTRY_FLAG_MAP[languageRegion]
  }
  const country = languageRegion.split('-')[1]
  return country
}

export function setLanguageLocaleCookie (locale) {
  if (!locale) return
  const language = languageFromLocale(locale)
  Cookies.set(LANGUAGE_COOKIE_NAME, language, {
    path: '/',
    expires: LANGUAGE_COOKIE_EXPIRY_DAYS,
    sameSite: 'Lax'
  })
}
